import { FormattedMessage } from 'react-intl';
import { RovoIcon } from '@atlaskit/logo';
import BoltSvg from './assets/bolt.svg';
import CompassSvg from './assets/compass.svg';
import Image from '@atlaskit/image';

import { type RecommendedFeature } from './types';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	rovoLogo: {
		// Since this is a logo and not an asset, we need to add the same spacing as the assets
		paddingInlineEnd: token('space.050'),
	},
});

type BannerVariantContent = {
	Icon: React.ComponentType;
	TitleMessage: React.FC<{ count: number }>;
	CtaMessage: React.FC;
};

function PlansTitleMessage({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.plans.title"
			defaultMessage="Keep your {count} projects on track"
			description="Title of the sidenav banner for the Plans variation, prompting free Jira users to upgrade."
			values={{ count }}
		/>
	);
}

function PlansCtaMessage() {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.plans.cta"
			defaultMessage="Try Plans"
			description="CTA link text on the sidenav banner for the Plans variation."
		/>
	);
}

function AutomationsTitleMessage({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.automations.title"
			defaultMessage="Keep your {count} rules running"
			description="Title of the sidenav banner for the Automations variation, prompting free Jira users to upgrade."
			values={{ count }}
		/>
	);
}

function AutomationsCtaMessage() {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.automations.cta"
			defaultMessage="Get more runs"
			description="CTA link text on the sidenav banner for the Automations variation."
		/>
	);
}

function RovoTitleMessage({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.rovo.title"
			defaultMessage="{count, plural, =0 {Stay across your team's tasks} one {Stay across your team's # task} other {Stay across your team's # tasks}}"
			description="Title of the sidenav banner for the Rovo variation, prompting free Jira users to upgrade."
			values={{ count }}
		/>
	);
}

function RovoCtaMessage() {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.placement.banner.rovo.cta"
			defaultMessage="Try Rovo"
			description="CTA link text on the sidenav banner for the Rovo variation."
		/>
	);
}

function RovoIconWrapper() {
	return (
		<Box xcss={styles.rovoLogo}>
			<RovoIcon label="" size="small" shouldUseHexLogo />
		</Box>
	);
}

function Compass() {
	return <Image src={CompassSvg} alt="" height={32} width={32} />;
}

function Bolt() {
	return <Image src={BoltSvg} alt="" height={32} width={32} />;
}

export const BANNER_CONTENT: Record<RecommendedFeature, BannerVariantContent> = {
	plans: {
		Icon: Compass,
		TitleMessage: PlansTitleMessage,
		CtaMessage: PlansCtaMessage,
	},
	automations: {
		Icon: Bolt,
		TitleMessage: AutomationsTitleMessage,
		CtaMessage: AutomationsCtaMessage,
	},
	rovo: {
		Icon: RovoIconWrapper,
		TitleMessage: RovoTitleMessage,
		CtaMessage: RovoCtaMessage,
	},
};
